import { createApi } from "@reduxjs/toolkit/query/react";
import { baseQueryWithArgs } from "../utils/reduxHelper";
import { trimClientOgData } from "../utils/common";

const buildCatalogQuery = ({
  brandId,
  includeRawData = false,
  isBrandSourcing = false,
  page,
  pageSize,
}) => {
  const creator = localStorage.getItem("username")?.replaceAll('"', "") || "";
  const params = new URLSearchParams({
    brand_id: brandId,
    include_raw_data: includeRawData.toString(),
    is_brand_sourcing: isBrandSourcing.toString(),
    ...(creator && { creator: creator }),
    ...(page && { page: page.toString() }),
    ...(pageSize && { page_size: pageSize.toString() }),
  });
  return `s/getManualSourcingCatalog?${params}`;
};

export const manualSourcingAPI = createApi({
  reducerPath: "manualSourcingAPI",
  baseQuery: baseQueryWithArgs,
  tagTypes: ["ManualCatalog", "SourcingTask"],
  endpoints: (builder) => ({
    submitManualSourcingLinksAsync: builder.mutation({
      query: ({ brandId, urls, isBrandSourcing = false, clientOgDataMap }) => {
        const trimmedClientOgDataMap = clientOgDataMap
          ? Object.fromEntries(
              Object.entries(clientOgDataMap).map(([url, data]) => [
                url,
                trimClientOgData(data),
              ])
            )
          : null;

        return {
          url: "s/submitManualSourcingLinksAsync",
          method: "POST",
          body: {
            brand_id: brandId,
            urls: urls.filter((url) => url.trim()),
            is_brand_sourcing: isBrandSourcing,
            ...(trimmedClientOgDataMap &&
              Object.keys(trimmedClientOgDataMap).length && {
                client_og_data_map: trimmedClientOgDataMap,
              }),
            creator_username:
              localStorage.getItem("username")?.replaceAll('"', "") || "",
          },
        };
      },
      transformResponse: (response) => {
        if (response?.success) {
          return response;
        } else {
          throw new Error(response?.error || "Failed to submit links");
        }
      },
    }),

    getScrapingTaskStatus: builder.query({
      query: (taskId) => {
        const creator =
          localStorage.getItem("username")?.replaceAll('"', "") || "";
        const params = new URLSearchParams({
          ...(creator && { creator_username: creator }),
        });
        return `s/scraping-task-status/${taskId}?${params}`;
      },
      providesTags: ["SourcingTask"],
      transformResponse: (response) => {
        if (response?.state) {
          return response;
        } else {
          throw new Error(response?.error || "Failed to get task status");
        }
      },
    }),

    getManualSourcingCatalog: builder.query({
      // page/page_size are optional. Without them the backend returns the whole catalog and
      // queues a rescrape for every item in it, so paginated callers pay for one page instead.
      query: buildCatalogQuery,
      // Pages of one catalog share a single cache entry, merged below. page_size stays in the
      // key: a caller asking for a different window size is asking a different question.
      serializeQueryArgs: ({ queryArgs, endpointName }) => {
        const { page, ...rest } = queryArgs || {};
        return { endpointName, ...rest };
      },
      merge: (currentCache, newData, { arg }) => {
        // Page 1 (and the unpaginated legacy call) replaces, so a refetch cannot duplicate rows.
        if (!arg?.page || arg.page === 1) {
          return newData;
        }
        // Upsert by id rather than append: re-reading a page the cache already holds must refresh
        // those rows, not concatenate a second copy of the window.
        const indexById = new Map(
          currentCache.catalog_items.map((item, index) => [item.id, index])
        );
        (newData.catalog_items || []).forEach((item) => {
          const index = indexById.get(item.id);
          if (index === undefined) {
            currentCache.catalog_items.push(item);
          } else {
            currentCache.catalog_items[index] = item;
          }
        });
        currentCache.summary = newData.summary;
        currentCache.meta = newData.meta;
      },
      forceRefetch: ({ currentArg, previousArg }) =>
        currentArg?.page !== previousArg?.page,
      // Deliberately NOT tagged. A tag refetch refires whatever page is current, which races the
      // page-1 replace that reloadCatalog issues. Callers refresh this endpoint by calling
      // reloadCatalog explicitly; the summary endpoint below keeps the tag.
      transformResponse: (response) => {
        if (response?.success) {
          return response;
        } else {
          throw new Error(response?.error || "Failed to get catalog");
        }
      },
    }),

    // Separate endpoint, and deliberately not the paginated one: callers here only need to know
    // whether the catalog is empty. Sharing getManualSourcingCatalog's cache entry would let a
    // one-item response overwrite the list page's loaded pages.
    getManualSourcingCatalogSummary: builder.query({
      query: (args) => buildCatalogQuery({ ...args, page: 1, pageSize: 1 }),
      providesTags: ["ManualCatalog"],
      transformResponse: (response) => {
        if (response?.success) {
          return response;
        } else {
          throw new Error(response?.error || "Failed to get catalog");
        }
      },
    }),

    addManualSourcingItemToCart: builder.mutation({
      query: ({ variantId, quantity = 1, brandSourcingBrandId = null }) => ({
        url: "s/addManualSourcingItemToCart",
        method: "POST",
        body: {
          variant_id: variantId,
          quantity: quantity,
          brand_sourcing_brand_id: brandSourcingBrandId,
          creator_username:
            localStorage.getItem("username")?.replaceAll('"', "") || "",
        },
      }),
      invalidatesTags: ["getCartProducts"],
      transformResponse: (response) => {
        if (response?.success) {
          return response;
        } else {
          throw new Error(response?.error || "Failed to add item to cart");
        }
      },
    }),

    updateManualCatalogItem: builder.mutation({
      query: ({
        catalogItemId, // Required - catalog item to update
        brandId,
        url,
        expandedUrl,
        clientOgData,
        title,
        price,
        size,
        color,
        imageFile,
        isBrandSourcing = false,
        triggerScraping = false,
        addToCart = true,
      }) => {
        if (imageFile && imageFile.type !== "image/png") {
          throw new Error("Only PNG images are allowed");
        }

        const formData = new FormData();
        formData.append("brand_id", brandId);
        formData.append("url", url);
        if (expandedUrl) formData.append("expanded_url", expandedUrl);
        if (clientOgData)
          formData.append(
            "client_og_data",
            JSON.stringify(trimClientOgData(clientOgData))
          );
        formData.append("title", title);
        formData.append("price", price);
        if (size !== undefined) formData.append("size", size);
        if (color !== undefined) formData.append("color", color);
        formData.append("is_brand_sourcing", isBrandSourcing.toString());
        formData.append("trigger_scraping", triggerScraping.toString());
        formData.append("add_to_cart", addToCart.toString());
        formData.append(
          "creator_username",
          localStorage.getItem("username")?.replaceAll('"', "") || ""
        );

        // catalog_item_id is always required for updates
        formData.append("catalog_item_id", catalogItemId);

        if (imageFile) {
          formData.append("image_file", imageFile);
        }

        return {
          url: "s/updateManualCatalogItem",
          method: "POST",
          body: formData,
        };
      },
      // The background rescrape refreshes one item's data without changing the catalog's
      // membership. Invalidating there would refetch the merged cache entry at page 1 and
      // throw away every page the creator has scrolled through.
      invalidatesTags: (result, error, arg) =>
        arg?.skipCatalogInvalidation ? [] : ["ManualCatalog"],
      transformResponse: (response) => {
        if (response?.success) {
          return response;
        } else {
          throw new Error(response?.error || "Failed to update catalog item");
        }
      },
    }),

    getSourcingConstant: builder.query({
      query: () => {
        const creator =
          localStorage.getItem("username")?.replaceAll('"', "") || "";
        const params = new URLSearchParams({
          ...(creator && { creator_username: creator }),
        });
        return `s/getSourcingConstant?${params}`;
      },
      transformResponse: (response) => {
        if (response?.success) {
          return response.data;
        } else {
          throw new Error(
            response?.message || "Failed to get sourcing constant"
          );
        }
      },
    }),
  }),
});

export const {
  useSubmitManualSourcingLinksAsyncMutation,
  useGetScrapingTaskStatusQuery,
  useLazyGetScrapingTaskStatusQuery,
  useGetManualSourcingCatalogQuery,
  useLazyGetManualSourcingCatalogQuery,
  useLazyGetManualSourcingCatalogSummaryQuery,
  useAddManualSourcingItemToCartMutation,
  useUpdateManualCatalogItemMutation,
  useGetSourcingConstantQuery,
  useLazyGetSourcingConstantQuery,
} = manualSourcingAPI;
